import React from "react";
import {Alert, Button, Card, Col, ListGroup, Row} from "react-bootstrap";
import SendControlPanel from "../SendControlPanel.jsx";
import {formatGermanDate, formatGermanDateTime} from "./scheduleForecast.js";

function isBodyEmpty(html) {
    if (!html) {
        return true;
    }
    const text = html.replace(/<[^>]*>/g, "").replace(/&nbsp;/g, " ").trim();
    return text.length === 0;
}

/**
 * Schritt 5 des Kampagnen-Assistenten: Zusammenfassung aller Einstellungen
 * in Klartext, eine Checkliste offener Punkte (mit Sprung zum betroffenen
 * Schritt) und der Start-Knopf - der ist erst aktiv, wenn kein Blocker
 * mehr offen ist.
 */
export default function StepPruefen({data, campaign, recipientsCount, mailboxes, signatures, goToStep, schedulePreview}) {
    const mailbox = (mailboxes ?? []).find((m) => m.email === data.sender_mailbox);
    const signature = (signatures ?? []).find((s) => String(s.id) === String(data.campaign_signature_id));

    const blockers = [];
    if (!data.sender_mailbox) {
        blockers.push({text: "Kein Absender-Postfach ausgewählt.", step: 1});
    }
    if (!data.subject?.trim()) {
        blockers.push({text: "Kein Betreff eingetragen.", step: 1});
    }
    if (isBodyEmpty(data.body_html)) {
        blockers.push({text: "Noch kein Mail-Text eingegeben.", step: 2});
    }
    if (!recipientsCount) {
        blockers.push({text: "Noch keine Empfänger importiert.", step: 3});
    }
    if (!data.send_start_date || !data.send_end_date) {
        blockers.push({text: "Kein Versand-Zeitraum festgelegt.", step: 4});
    }
    if (!data.send_weekdays || data.send_weekdays.length === 0) {
        blockers.push({text: "Kein Wochentag für den Versand ausgewählt.", step: 4});
    }

    return (
        <>
            <Card className="mb-3">
                <Card.Body>
                    <p className="text-muted">
                        Letzter Schritt: hier siehst du alle Einstellungen auf einen
                        Blick. Sind alle Punkte unten grün, kannst du den Versand
                        starten.
                    </p>

                    <Row className="mb-2">
                        <Col sm={3} className="text-muted">Absender</Col>
                        <Col sm={9}>
                            {mailbox ? (mailbox.display_name ? `${mailbox.display_name} <${mailbox.email}>` : mailbox.email) : "- nicht gewählt -"}
                            {data.sender_name ? ` (angezeigt als „${data.sender_name}")` : ""}
                        </Col>
                    </Row>
                    <Row className="mb-2">
                        <Col sm={3} className="text-muted">Betreff</Col>
                        <Col sm={9}>{data.subject || "- kein Betreff -"}</Col>
                    </Row>
                    <Row className="mb-2">
                        <Col sm={3} className="text-muted">Signatur</Col>
                        <Col sm={9}>{signature ? signature.name : "- keine -"}</Col>
                    </Row>
                    <Row className="mb-2">
                        <Col sm={3} className="text-muted">Anhang</Col>
                        <Col sm={9}>
                            {data.attachment?.name || campaign?.attachment_original_name || "- kein Anhang -"}
                        </Col>
                    </Row>
                    <Row className="mb-2">
                        <Col sm={3} className="text-muted">Empfänger</Col>
                        <Col sm={9}>{recipientsCount ?? 0}</Col>
                    </Row>
                    <Row className="mb-2">
                        <Col sm={3} className="text-muted">Zeitraum</Col>
                        <Col sm={9}>
                            {data.send_start_date && data.send_end_date
                                ? `${formatGermanDate(data.send_start_date)} bis ${formatGermanDate(data.send_end_date)}`
                                : "- kein Zeitraum -"}
                        </Col>
                    </Row>
                    <Row>
                        <Col sm={3} className="text-muted">Versandplan</Col>
                        <Col sm={9}>
                            {schedulePreview && schedulePreview.geplant > 0 ? (
                                <>
                                    {schedulePreview.geplant} Mail(s) eingeplant, erste am{" "}
                                    {formatGermanDateTime(schedulePreview.erster)}, letzte am{" "}
                                    {formatGermanDateTime(schedulePreview.letzter)}.
                                </>
                            ) : (
                                "- noch kein Versandplan berechnet -"
                            )}
                        </Col>
                    </Row>
                </Card.Body>
            </Card>

            {schedulePreview && (schedulePreview.nicht_planbar > 0 || schedulePreview.ungeplant_wegen_limit > 0) && (
                <Alert variant="warning" className="mb-3">
                    <strong>Achtung:</strong> nicht alle Empfänger konnten eingeplant werden - der Versand kann
                    trotzdem gestartet werden, aber nicht jeder Empfänger bekommt eine Mail.
                    {schedulePreview.nicht_planbar > 0 && (
                        <div>{schedulePreview.nicht_planbar} Empfänger passen in keinen Versandtag mehr (Zeitraum zu kurz).</div>
                    )}
                    {schedulePreview.ungeplant_wegen_limit > 0 && (
                        <div>{schedulePreview.ungeplant_wegen_limit} Empfänger bleiben wegen der Gesamtmenge-Obergrenze unversendet.</div>
                    )}
                </Alert>
            )}

            <Card className="mb-3">
                <Card.Body>
                    <Card.Title>Offene Punkte</Card.Title>
                    {blockers.length === 0 ? (
                        <Alert variant="success" className="mb-0">
                            Alles vollständig - die Kampagne kann gestartet werden.
                        </Alert>
                    ) : (
                        <ListGroup>
                            {blockers.map((blocker, i) => (
                                <ListGroup.Item key={i} className="d-flex justify-content-between align-items-center">
                                    <span className="text-danger">{blocker.text}</span>
                                    <Button size="sm" variant="outline-secondary" onClick={() => goToStep(blocker.step)}>
                                        Zu Schritt {blocker.step}
                                    </Button>
                                </ListGroup.Item>
                            ))}
                        </ListGroup>
                    )}
                </Card.Body>
            </Card>

            {campaign && (
                <SendControlPanel
                    campaign={campaign}
                    disableStart={blockers.length > 0}
                    disableStartReason="Erst alle offenen Punkte oben beheben."
                />
            )}
        </>
    );
}
